import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Truck, 
  MapPin, 
  Navigation, 
  CheckCircle2, 
  Phone,
  Printer,
  Camera,
  CircleDashed,
  SkipForward,
  XCircle,
  AlertCircle,
  PackageOpen,
  MessageSquare,
  Loader2,
  RotateCcw
} from "lucide-react";
import { LoadTasksMap } from "@/components/loads/LoadTasksMap";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import { getCustomerDisplayName, getLoadDateLabel } from "@/lib/formatters";
import { 
  LOAD_STATUS_LABELS, 
  LOAD_STATUS_COLORS,
  INSTALLATION_STATUS_LABELS,
  INSTALLATION_STATUS_BADGE_COLORS,
  LoadStatus,
  InstallationStatus,
} from "@/config/constants";
import { openPrintableRouteList } from "@/components/loads/PrintableRouteList";
import { InstallationPhotos } from "@/components/installations/InstallationPhotos";
import { DEPOT_LOCATION } from "@/config/locations";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { InstallationWithRelations } from "@/types/installations";

interface Load {
  id: string;
  load_date: string;
  status: string;
  total_distance_km?: number | null;
  estimated_duration_min?: number | null;
  notes?: string | null;
  route_geometry?: unknown;
  [key: string]: unknown;
}

interface MyLoadDetailViewProps {
  load: Load | undefined;
  installations: InstallationWithRelations[];
  onBack: () => void;
  onUpdateLoadStatus: (loadId: string, status: LoadStatus) => void;
  onUpdateInstallationStatus: (installationId: string, status: InstallationStatus, notes?: string, updatedAt?: string) => void;
  onSendSms: (installationId: string, orderId: string) => void;
  sendingSmsFor: string | null;
  notifiedInstallations: Set<string>;
  isUpdatePending: boolean;
}


function getGoogleMapsUrl(installations: InstallationWithRelations[]): string {
  if (!installations || installations.length === 0) return "";
  
  const waypoints = installations
    .filter(inst => inst.cemetery?.latitude && inst.cemetery?.longitude)
    .map(inst => `${inst.cemetery?.latitude},${inst.cemetery?.longitude}`);
  
  return `https://www.google.com/maps/dir/${DEPOT_LOCATION.lat},${DEPOT_LOCATION.lng}/${waypoints.join('/')}/${DEPOT_LOCATION.lat},${DEPOT_LOCATION.lng}`;
}

export function MyLoadDetailView({
  load,
  installations,
  onBack,
  onUpdateLoadStatus,
  onUpdateInstallationStatus,
  onSendSms,
  sendingSmsFor,
  notifiedInstallations,
  isUpdatePending,
}: MyLoadDetailViewProps) {
  const [photoInstallationId, setPhotoInstallationId] = useState<string | null>(null);
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
  const [statusDialog, setStatusDialog] = useState<{
    installationId: string;
    status: InstallationStatus;
  } | null>(null);
  const [statusReason, setStatusReason] = useState("");

  const completedCount = installations.filter(i => i.status === "completed").length;
  const partialCount = installations.filter(i => i.status === "partial").length;
  const skippedCount = installations.filter(i => i.status === "skipped").length;
  const pendingCount = installations.filter(i => i.status === "pending" || i.status === "in_progress").length;
  const allHandled = pendingCount === 0 && installations.length > 0;
  const hasUnfinished = partialCount > 0 || skippedCount > 0;

  const handleTaskClick = (taskId: string) => {
    setHighlightedTaskId(taskId);
    const element = document.getElementById(`task-card-${taskId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => setHighlightedTaskId(null), 2000);
  };

  const handleStatusChange = (installationId: string, status: InstallationStatus) => {
    // Find the installation to get its updated_at for conflict detection
    const installation = installations.find(i => i.id === installationId);
    const updatedAt = installation?.updated_at;
    
    if (status === "partial" || status === "skipped") {
      setStatusDialog({ installationId, status });
      setStatusReason("");
    } else {
      onUpdateInstallationStatus(installationId, status, undefined, updatedAt);
    }
  };

  const handleStatusDialogSubmit = () => {
    if (!statusDialog) return;
    // Find the installation to get its updated_at for conflict detection
    const installation = installations.find(i => i.id === statusDialog.installationId);
    const updatedAt = installation?.updated_at;
    onUpdateInstallationStatus(statusDialog.installationId, statusDialog.status, statusReason, updatedAt);
    setStatusDialog(null);
    setStatusReason("");
  };

  const handlePrintLoad = () => {
    if (!load) return;
    const routeItems = installations.map((inst, index) => ({
      position: inst.load_position || index + 1,
      orderNumber: inst.order?.order_number || "",
      customerName: getCustomerDisplayName(inst.order?.customer),
      cemeteryName: inst.cemetery?.name || "",
      cemeteryCity: inst.cemetery?.city || "",
      taskType: inst.task_type || "",
      lat: inst.cemetery?.latitude || 0,
      lng: inst.cemetery?.longitude || 0,
    }));

    openPrintableRouteList({
      loadDate: load.load_date,
      installerName: "Minä",
      notes: load.notes || "",
      routeItems,
      totalDistanceKm: load.total_distance_km ?? undefined,
      estimatedDurationMin: load.estimated_duration_min ?? undefined,
      depotName: DEPOT_LOCATION.name,
    });
  };

  if (!load) {
    return (
      <div className="space-y-4 max-w-lg mx-auto">
        <Button variant="ghost" onClick={onBack}>← Takaisin</Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Kuormaa ei löytynyt</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          ← Takaisin
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePrintLoad}
        >
          <Printer className="h-4 w-4" />
        </Button>
      </div>

      {/* Load info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="h-5 w-5" />
                {getLoadDateLabel(load.load_date)}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {format(new Date(load.load_date), "d. MMMM yyyy", { locale: fi })}
              </p>
            </div>
            <Badge className={LOAD_STATUS_COLORS[load.status as LoadStatus]}>
              {LOAD_STATUS_LABELS[load.status as LoadStatus]}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 rounded-lg bg-muted">
              <div className="text-2xl font-bold">{installations.length}</div>
              <div className="text-xs text-muted-foreground">Kohteita</div>
            </div>
            {load.total_distance_km && (
              <div className="p-3 rounded-lg bg-muted">
                <div className="text-2xl font-bold">{Math.round(load.total_distance_km)}</div>
                <div className="text-xs text-muted-foreground">km</div>
              </div>
            )}
            {load.estimated_duration_min && (
              <div className="p-3 rounded-lg bg-muted">
                <div className="text-2xl font-bold">
                  {Math.floor(load.estimated_duration_min / 60)}h
                </div>
                <div className="text-xs text-muted-foreground">Ajoaika</div>
              </div>
            )}
          </div>

          {/* Progress */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Edistyminen</span>
              <span className="flex gap-2">
                {completedCount > 0 && <span className="text-green-600">{completedCount} valmis</span>}
                {partialCount > 0 && <span className="text-orange-600">{partialCount} osittain</span>}
                {skippedCount > 0 && <span className="text-red-600">{skippedCount} ohitettu</span>}
                {pendingCount > 0 && <span className="text-muted-foreground">{pendingCount} jäljellä</span>}
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden flex">
              <div 
                className="h-full bg-green-500 transition-all"
                style={{ width: `${installations.length ? (completedCount / installations.length) * 100 : 0}%` }}
              />
              <div 
                className="h-full bg-orange-500 transition-all"
                style={{ width: `${installations.length ? (partialCount / installations.length) * 100 : 0}%` }}
              />
              <div 
                className="h-full bg-red-500 transition-all"
                style={{ width: `${installations.length ? (skippedCount / installations.length) * 100 : 0}%` }}
              />
            </div>
          </div>

          {/* Task map */}
          <div className="h-48 rounded-lg overflow-hidden border">
            <LoadTasksMap
              tasks={installations.map(inst => ({
                id: inst.id,
                status: inst.status as "pending" | "in_progress" | "completed" | "partial" | "skipped" | "cancelled",
                task_type: (inst.task_type || "installation") as "installation" | "delivery" | "maintenance" | "pickup",
                load_position: inst.load_position ?? null,
                latitude: inst.cemetery?.latitude ?? null,
                longitude: inst.cemetery?.longitude ?? null,
                cemetery: inst.cemetery ? {
                  name: inst.cemetery.name || "",
                  city: inst.cemetery.city || "",
                  address: inst.cemetery.address || "",
                  latitude: inst.cemetery.latitude ?? null,
                  longitude: inst.cemetery.longitude ?? null,
                } : null,
                order: inst.order ? {
                  order_number: inst.order.order_number || "",
                  order_type: (inst.order.order_type || "new_work") as "new_work" | "pickup_work" | "field_work",
                  customer: inst.order.customer ? {
                    first_name: inst.order.customer.first_name ?? null,
                    last_name: inst.order.customer.last_name ?? null,
                    company_name: inst.order.customer.company_name ?? null,
                  } : null,
                } : null,
              }))}
              depotLocation={DEPOT_LOCATION}
              routeGeometry={load.route_geometry}
              onTaskClick={handleTaskClick}
            />
          </div>

          {/* Navigation button */}
          <a
            href={getGoogleMapsUrl(installations)}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button className="w-full gap-2" size="lg">
              <Navigation className="h-5 w-5" />
              Avaa navigaattorissa
            </Button>
          </a>

          {/* Status buttons */}
          {load.status === "planned" && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onUpdateLoadStatus(load.id, "in_progress")}
            >
              Aloita kuorma
            </Button>
          )}
          {load.status === "in_progress" && allHandled && (
            <Button
              variant={hasUnfinished ? "outline" : "default"}
              className={`w-full ${hasUnfinished ? "border-orange-500 text-orange-700 hover:bg-orange-50" : ""}`}
              onClick={() => onUpdateLoadStatus(load.id, "completed")}
            >
              {hasUnfinished ? (
                <>
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Sulje kuorma ({completedCount}/{installations.length} valmis)
                </>
              ) : (
                "Merkitse kuorma valmiiksi"
              )}
            </Button>
          )}

          {load.notes && (
            <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-sm">
              <strong>Huom:</strong> {load.notes}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task list */}
      <div className="space-y-3">
        <h2 className="font-semibold flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Kohteet ({installations.length})
        </h2>

        {installations.map((inst, index) => {
          const isCompleted = inst.status === "completed";
          const isPartial = inst.status === "partial";
          const isSkipped = inst.status === "skipped";
          const isHandled = isCompleted || isPartial || isSkipped || inst.status === "cancelled";
          const customer = inst.order?.customer;
          const mapsUrl = inst.cemetery?.latitude && inst.cemetery?.longitude
            ? `https://www.google.com/maps/search/?api=1&query=${inst.cemetery.latitude},${inst.cemetery.longitude}`
            : "";

          const getStatusIcon = () => {
            if (isCompleted) return <CheckCircle2 className="h-5 w-5" />;
            if (isPartial) return <CircleDashed className="h-5 w-5" />;
            if (isSkipped) return <SkipForward className="h-5 w-5" />;
            if (inst.status === "cancelled") return <XCircle className="h-5 w-5" />;
            if (inst.task_type === "installation" && inst.order?.order_type === "pickup_work") {
              return <RotateCcw className="h-5 w-5" />;
            }
            if (inst.task_type === "installation" && inst.order?.order_type === "field_work") {
              return <span className="text-sm">🔧</span>;
            }
            if (inst.task_type === "pickup") {
              return <PackageOpen className="h-5 w-5" />;
            }
            return inst.load_position || index + 1;
          };

          const getStatusBgColor = () => {
            if (isCompleted) return "bg-green-500 text-white";
            if (isPartial) return "bg-orange-500 text-white";
            if (isSkipped) return "bg-red-500 text-white";
            if (inst.status === "cancelled") return "bg-gray-500 text-white";
            if (inst.task_type === "installation" && inst.order?.order_type === "pickup_work") {
              return "bg-teal-500 text-white";
            }
            if (inst.task_type === "installation" && inst.order?.order_type === "field_work") {
              return "bg-purple-500 text-white";
            }
            if (inst.task_type === "pickup") {
              return "bg-orange-500 text-white";
            }
            return "bg-primary text-primary-foreground";
          };

          return (
            <Card 
              id={`task-card-${inst.id}`}
              key={inst.id} 
              className={`transition-all duration-300 ${isHandled ? "opacity-60" : ""} ${highlightedTaskId === inst.id ? "ring-2 ring-primary ring-offset-2 bg-primary/5" : ""}`}
            >
              <CardContent className="p-4">
                <div className="flex gap-3">
                  {/* Position number */}
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shrink-0
                    ${getStatusBgColor()}
                  `}>
                    {getStatusIcon()}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium">
                          {inst.order?.order_number}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {getCustomerDisplayName(customer)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {inst.task_type === "installation" && inst.order?.order_type === "pickup_work" ? (
                          <Badge className="shrink-0 bg-teal-100 text-teal-800 border-teal-300 gap-1">
                            <RotateCcw className="h-3 w-3" />
                            Palautus
                          </Badge>
                        ) : inst.task_type === "installation" && inst.order?.order_type === "field_work" ? (
                          <Badge className="shrink-0 bg-purple-100 text-purple-800 border-purple-300 gap-1">
                            🔧 Kenttätyö
                          </Badge>
                        ) : inst.task_type === "pickup" ? (
                          <Badge className="shrink-0 bg-orange-100 text-orange-800 border-orange-300 gap-1">
                            <PackageOpen className="h-3 w-3" />
                            Nouto
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="shrink-0">
                            {inst.task_type === "installation" ? "Asennus" : 
                             inst.task_type === "delivery" ? "Toimitus" : inst.task_type}
                          </Badge>
                        )}
                        {isHandled && (
                          <Badge className={INSTALLATION_STATUS_BADGE_COLORS[inst.status as InstallationStatus]}>
                            {INSTALLATION_STATUS_LABELS[inst.status as InstallationStatus]}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="mt-2 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {inst.cemetery?.name}, {inst.cemetery?.city}
                      </div>
                      {inst.cemetery?.address && (
                        <div className="text-xs text-muted-foreground ml-4">
                          {inst.cemetery.address}
                        </div>
                      )}
                      {inst.notes && isHandled && (
                        <div className="mt-1 text-xs text-muted-foreground italic">
                          {inst.notes}
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {mapsUrl && (
                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="gap-1">
                            <Navigation className="h-3 w-3" />
                            Navigoi
                          </Button>
                        </a>
                      )}
                      {customer?.phone && (
                        <a href={`tel:${customer.phone}`}>
                          <Button variant="outline" size="sm" className="gap-1">
                            <Phone className="h-3 w-3" />
                            Soita
                          </Button>
                        </a>
                      )}
                      {customer?.phone && !isHandled && (
                        <Button
                          variant={notifiedInstallations.has(inst.id) ? "ghost" : "outline"}
                          size="sm"
                          className={`gap-1 ${notifiedInstallations.has(inst.id) ? "text-green-600" : ""}`}
                          disabled={sendingSmsFor === inst.id || notifiedInstallations.has(inst.id)}
                          onClick={() => inst.order?.id && onSendSms(inst.id, inst.order.id)}
                        >
                          {sendingSmsFor === inst.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : notifiedInstallations.has(inst.id) ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <MessageSquare className="h-3 w-3" />
                          )}
                          {notifiedInstallations.has(inst.id) ? "Ilmoitettu" : "Ilmoita"}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        onClick={() => setPhotoInstallationId(inst.id)}
                      >
                        <Camera className="h-3 w-3" />
                        Kuvat
                      </Button>
                      {!isHandled && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" className="gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Merkitse
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleStatusChange(inst.id, "completed")}>
                              <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                              Valmis
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(inst.id, "partial")}>
                              <CircleDashed className="h-4 w-4 mr-2 text-orange-600" />
                              Osittain valmis
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(inst.id, "skipped")}>
                              <SkipForward className="h-4 w-4 mr-2 text-red-600" />
                              Ohitettu
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Photo Sheet */}
      <Sheet open={!!photoInstallationId} onOpenChange={() => setPhotoInstallationId(null)}>
        <SheetContent side="bottom" className="h-[85vh]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Asennuskuvat
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 overflow-y-auto max-h-[calc(85vh-80px)]">
            {photoInstallationId && (
              <InstallationPhotos installationId={photoInstallationId} />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Status reason dialog */}
      <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {statusDialog?.status === "partial" ? "Syy keskeneräisyydelle" : "Syy ohitukselle"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">
                Kirjoita lyhyt selitys miksi tehtävä jäi {statusDialog?.status === "partial" ? "osittain valmiiksi" : "tekemättä"}
              </Label>
              <Textarea
                id="reason"
                placeholder={statusDialog?.status === "partial" 
                  ? "Esim. Työ aloitettu mutta perusta täytyy kuivua ennen jatkamista" 
                  : "Esim. Asiakas ei ollut paikalla, hautausmaalle ei päässyt"}
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)}>
              Peruuta
            </Button>
            <Button 
              onClick={handleStatusDialogSubmit}
              disabled={!statusReason.trim()}
            >
              Tallenna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
